<script>
import axios from 'axios';

export default {
  name: 'PainelNotas',
  props: {
    contato: { type: Object, required: true },
  },
  data() {
    return {
      notas: [],
      novaNota: '',
      carregando: true,
      salvando: false,
      erro: null,
    };
  },
  watch: {
    'contato.id': {
      handler() { this.carregar(); },
      immediate: false,
    },
  },
  async mounted() {
    await this.carregar();
  },
  methods: {
    async carregar() {
      this.carregando = true;
      this.erro = null;
      try {
        const { data } = await axios.get(`/contato/${this.contato.id}/nota`);
        this.notas = Array.isArray(data) ? data : [];
      } catch (error) {
        const msg = (error.response && error.response.data && (error.response.data.details || error.response.data.error)) || error.message;
        this.erro = msg;
      } finally {
        this.carregando = false;
      }
    },
    async criar() {
      const conteudo = (this.novaNota || '').trim();
      if (!conteudo || this.salvando) return;
      this.salvando = true;
      this.erro = null;
      try {
        await axios.post(`/contato/${this.contato.id}/nota`, {
          contato_id: this.contato.id,
          conteudo,
        });
        this.novaNota = '';
        await this.carregar();
      } catch (error) {
        this.erro = (error.response && error.response.data && error.response.data.error) || error.message;
      } finally {
        this.salvando = false;
      }
    },
    async fixar(nota) {
      try {
        await axios.put(`/contato/nota/${nota.id}`, { fixada: !nota.fixada });
        await this.carregar();
      } catch (error) {
        this.erro = error.message;
      }
    },
    async remover(nota) {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Remover esta nota?')) return;
      try {
        await axios.delete(`/contato/nota/${nota.id}`);
        await this.carregar();
      } catch (error) {
        this.erro = error.message;
      }
    },
    formatarData(d) {
      if (!d) return '';
      try { return new Date(d).toLocaleString('pt-BR'); } catch (_e) { return ''; }
    },
  },
};
</script>

<template>
  <div class="painel-notas">
    <h6 class="mb-3"><i class="ri-sticky-note-line me-1"></i>Notas internas</h6>
    <small class="text-muted d-block mb-3">
      Visíveis apenas no CRM. <strong>Não</strong> são enviadas pelo WhatsApp.
    </small>

    <div v-if="erro" class="alert alert-danger py-2 small">{{ erro }}</div>

    <!-- Nova nota -->
    <div class="mb-3">
      <textarea
        v-model="novaNota"
        class="form-control form-control-sm"
        rows="3"
        placeholder="Anote algo sobre este contato..."
      ></textarea>
      <button type="button" class="btn btn-primary btn-sm w-100 mt-2" :disabled="!novaNota.trim() || salvando" @click="criar">
        <i v-if="salvando" class="ri-loader-4-line spin me-1"></i>
        <i v-else class="ri-add-line me-1"></i>{{ salvando ? 'Salvando...' : 'Adicionar nota' }}
      </button>
    </div>

    <hr class="my-3" />

    <div v-if="carregando" class="text-muted small">Carregando notas...</div>
    <div v-else-if="!notas.length" class="text-muted small fst-italic">Nenhuma nota ainda.</div>
    <div v-else class="notas-lista">
      <article v-for="n in notas" :key="n.id" class="nota-card" :class="{ fixada: n.fixada }">
        <div class="nota-conteudo">{{ n.conteudo }}</div>
        <div class="nota-rodape d-flex justify-content-between align-items-center mt-1">
          <small class="text-muted">
            <i v-if="n.fixada" class="ri-pushpin-fill text-warning me-1"></i>
            <span v-if="n.autor">{{ n.autor.nome || n.autor.name || `#${n.autor.id}` }} · </span>
            {{ formatarData(n.created_at) }}
          </small>
          <div class="nota-acoes">
            <button type="button" class="btn-acao" :title="n.fixada ? 'Desfixar' : 'Fixar no topo'" @click="fixar(n)">
              <i :class="n.fixada ? 'ri-pushpin-fill' : 'ri-pushpin-line'"></i>
            </button>
            <button type="button" class="btn-acao" title="Remover" @click="remover(n)">
              <i class="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<style scoped>
.notas-lista { display: flex; flex-direction: column; gap: 0.5rem; }
.nota-card {
  padding: 0.6rem 0.75rem;
  background: var(--vz-body-bg);
  border: 1px solid var(--vz-border-color);
  border-radius: 0.375rem;
}
.nota-card.fixada { border-left: 3px solid var(--vz-warning); }
.nota-conteudo { font-size: 0.85rem; white-space: pre-wrap; word-break: break-word; }
.nota-acoes { display: flex; gap: 0.25rem; }
.btn-acao {
  background: transparent;
  border: 0;
  color: var(--vz-secondary-color);
  cursor: pointer;
  padding: 0.15rem 0.35rem;
  border-radius: 0.2rem;
  font-size: 0.85rem;
}
.btn-acao:hover { color: var(--vz-primary); background: var(--vz-tertiary-bg); }
.spin { display: inline-block; animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
</style>
